import React, { useState, useEffect, useRef } from 'react';

const LANGUAGES = [
  { code: 'en', native: 'English', english: 'English' },
  { code: 'zh-CN', native: '中文', english: 'Chinese' },
  { code: 'hi', native: 'हिन्दी', english: 'Hindi' },
  { code: 'es', native: 'Español', english: 'Spanish' },
  { code: 'fr', native: 'Français', english: 'French' },
  { code: 'ar', native: 'العربية', english: 'Arabic' },
  { code: 'bn', native: 'বাংলা', english: 'Bengali' },
  { code: 'pt', native: 'Português', english: 'Portuguese' },
  { code: 'ru', native: 'Русский', english: 'Russian' },
  { code: 'ja', native: '日本語', english: 'Japanese' },
];

let initPromise = null;

function ensureGoogleTranslate() {
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve) => {
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { pageLanguage: 'en', autoDisplay: false },
        'google_translate_element'
      );
      const check = setInterval(() => {
        if (document.querySelector('.goog-te-combo')) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    };
    const s = document.createElement('script');
    s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    document.body.appendChild(s);
  });

  return initPromise;
}

function triggerTranslation(langCode) {
  if (langCode === 'en') {
    const host = window.location.hostname;
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${host}`;
    localStorage.removeItem('preferredLang');
    window.location.reload();
    return;
  }

  localStorage.setItem('preferredLang', langCode);

  ensureGoogleTranslate().then(() => {
    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event('change'));
    }
  });
}

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export const LanguageSelector = ({ mobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(
    () => localStorage.getItem('preferredLang') || 'en'
  );
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setSelected(code);
    setIsOpen(false);
    triggerTranslation(code);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select language"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: mobile ? '12px 0' : '10px 12px',
          color: '#272726',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: mobile ? '18px' : '15px',
          fontWeight: '500',
          borderRadius: '6px',
          transition: 'background-color 0.25s ease',
        }}
        onMouseEnter={(e) => {
          if (!mobile) e.currentTarget.style.backgroundColor = 'rgba(39,39,38,0.06)';
        }}
        onMouseLeave={(e) => {
          if (!mobile) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <GlobeIcon />
        {mobile && <span>Language</span>}
      </button>

      {isOpen && (
        <div
          style={{
            position: mobile ? 'relative' : 'absolute',
            top: mobile ? '0' : '100%',
            right: mobile ? 'auto' : '0',
            marginTop: '4px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: mobile ? 'none' : '0 4px 20px rgba(0,0,0,0.12)',
            border: mobile ? 'none' : '1px solid rgba(39,39,38,0.08)',
            minWidth: '200px',
            zIndex: 1002,
            overflow: 'hidden',
          }}
        >
          {LANGUAGES.map(({ code, native, english }) => (
            <button
              key={code}
              onClick={() => handleSelect(code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '10px 16px',
                border: 'none',
                background: selected === code
                  ? 'rgba(39,39,38,0.06)'
                  : 'transparent',
                cursor: 'pointer',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontSize: '14px',
                color: '#272726',
                textAlign: 'left',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(39,39,38,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  selected === code ? 'rgba(39,39,38,0.06)' : 'transparent';
              }}
            >
              <span style={{ fontWeight: '500' }}>{native}</span>
              <span style={{ fontSize: '12px', color: '#888', marginLeft: '12px' }}>
                {english}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
